import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Alert,
  Button,
  Snackbar,
  useTheme,
  SelectChangeEvent,
} from '@mui/material';
import {
  Refresh,
  Email,
  Phone,
  ExpandMore,
  ExpandLess,
  FilterList,
} from '@mui/icons-material';
import {
  getContactSubmissions,
  updateContactStatus,
  ContactSubmission,
} from '@services/ContactSubmissionsService';

type StatusFilter = 'all' | 'pending' | 'new' | 'contacted' | 'resolved';

const MagnetixContactSubmissions: React.FC = () => {
  const theme = useTheme();
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = statusFilter !== 'all' ? { status: statusFilter as 'pending' | 'new' | 'contacted' | 'resolved' } : undefined;
      const data = await getContactSubmissions(filters);
      setSubmissions(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch contact submissions');
      showSnackbar('Failed to load contact submissions', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [statusFilter]);

  const handleStatusChange = async (id: number, newStatus: 'pending' | 'new' | 'contacted' | 'resolved') => {
    try {
      setUpdatingId(id);
      await updateContactStatus(id, newStatus);
      setSubmissions(prev =>
        prev.map(sub => (sub.id === id ? { ...sub, status: newStatus } : sub))
      );
      showSnackbar('Status updated successfully', 'success');
    } catch (err: any) {
      showSnackbar('Failed to update status', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateMessage = (message: string, maxLength: number = 80) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value as StatusFilter);
  };

  if (loading && submissions.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Magnetix Contact Submissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage contact form submissions from Magnetix website
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchSubmissions}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FilterList color="action" />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            label="Status Filter"
            onChange={handleFilterChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="new">New</MenuItem>
            <MenuItem value="contacted">Contacted</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          Total: {submissions.length} submission{submissions.length !== 1 ? 's' : ''}
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={fetchSubmissions}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      )}

      {/* Table */}
      {submissions.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Submissions Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {statusFilter === 'all'
              ? 'There are no contact submissions yet.'
              : `No submissions with status "${statusFilter}".`}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.action.hover }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((submission) => (
                <React.Fragment key={submission.id}>
                  <TableRow hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {submission.first_name} {submission.last_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Email fontSize="small" color="action" />
                          <Typography
                            variant="body2"
                            component="a"
                            href={`mailto:${submission.email}`}
                            sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {submission.email}
                          </Typography>
                        </Box>
                        {submission.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {submission.phone}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={submission.subject}
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 250 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {expandedRow === submission.id
                            ? submission.message
                            : truncateMessage(submission.message)}
                        </Typography>
                        {submission.message.length > 80 && (
                          <IconButton
                            size="small"
                            onClick={() => setExpandedRow(expandedRow === submission.id ? null : submission.id)}
                          >
                            {expandedRow === submission.id ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={submission.source}
                        size="small"
                        variant="outlined"
                        color="default"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {formatDate(submission.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {updatingId === submission.id ? (
                        <CircularProgress size={24} />
                      ) : (
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={submission.status}
                            onChange={(e) =>
                              handleStatusChange(
                                submission.id,
                                e.target.value as 'pending' | 'new' | 'contacted' | 'resolved'
                              )
                            }
                            sx={{
                              '& .MuiSelect-select': {
                                py: 0.5,
                              },
                            }}
                          >
                            <MenuItem value="pending">
                              <Chip label="Pending" size="small" color="default" />
                            </MenuItem>
                            <MenuItem value="new">
                              <Chip label="New" size="small" color="warning" />
                            </MenuItem>
                            <MenuItem value="contacted">
                              <Chip label="Contacted" size="small" color="info" />
                            </MenuItem>
                            <MenuItem value="resolved">
                              <Chip label="Resolved" size="small" color="success" />
                            </MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MagnetixContactSubmissions;
